import React, { memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { FilePicker } from '@Sage/hoi-poi-ui';

import { BillboardActions } from 'actions';
import { IMAGE_EXTENSIONS, IMAGES_FILES_VALID_FORMATS } from 'constants/Constants';
import FieldWrapper from 'containers/components/Fields/FieldWrapper';
import { getLiteral } from 'utils/getLiteral';

function getFileExtension(fileName = '') {
    const parts = String(fileName).split('.');
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
}

function getPickerFileData(file) {
    if (file?.file instanceof File) return file.file;
    if (file instanceof File) return file;
    return file?.file || file;
}

function isImageFile(file) {
    const data = getPickerFileData(file);
    if (!data) return false;

    if (data.type && IMAGES_FILES_VALID_FORMATS.includes(data.type)) {
        return true;
    }

    const extension = data.Extension?.toLowerCase?.() || getFileExtension(data.name);
    return Boolean(extension && IMAGE_EXTENSIONS.includes(extension));
}

function isDocumentFile(file) {
    return !isImageFile(file);
}

function mapToPickerFiles(files) {
    return files.map((file, index) => {
        if (file instanceof File) {
            return {
                id: `new-${file.name}-${file.size}-${index}`,
                file,
            };
        }

        if (file?.id) {
            const extension = getFileExtension(file.description || file.name || '');
            const displayName = file.description ? `${file.description}.${extension}` : file.name;
            let mimeType = file.type;
            if (!mimeType && extension && IMAGE_EXTENSIONS.includes(extension)) {
                mimeType = `image/${extension === 'jpg' ? 'jpeg' : extension}`;
            }

            return {
                id: file.id,
                file: {
                    ...file,
                    name: displayName || file.name,
                    size: file.size,
                    type: mimeType || file.type,
                },
            };
        }

        return {
            id: `new-${index}`,
            file,
        };
    });
}

const BillboardAttachmentsField = memo(({ data, field }) => {
    const dispatch = useDispatch();

    const newFilesImmutable = useSelector((state) => state.billboard.crud.get('files'));
    const filesToRemoveImmutable = useSelector((state) =>
        state.billboard.crud.get('filesToRemove'),
    );

    const newFiles = useMemo(() => newFilesImmutable.toJS(), [newFilesImmutable]);
    const filesToRemove = useMemo(() => filesToRemoveImmutable.toJS(), [filesToRemoveImmutable]);

    const files = useMemo(() => {
        let existingFiles = data?.attachmentFiles || [];
        filesToRemove.forEach((idFile) => {
            existingFiles = existingFiles.filter((file) => file.id !== idFile);
        });
        return [...existingFiles, ...newFiles];
    }, [data?.attachmentFiles, filesToRemove, newFiles]);

    const pickerFiles = useMemo(() => mapToPickerFiles(files), [files]);

    const onDrop = useCallback(
        (acceptedFiles) => {
            if (acceptedFiles?.length) {
                dispatch(BillboardActions.addFilesToUpload(acceptedFiles));
            }
        },
        [dispatch],
    );

    const onRemove = useCallback(
        (removedFile) => {
            const rawFile = removedFile?.file || removedFile;
            const serverId = removedFile?.id ?? rawFile?.id;

            if (serverId && !String(serverId).startsWith('new-')) {
                dispatch(BillboardActions.addFileToRemove(serverId));
                return;
            }

            const index = newFiles.findIndex(
                (file) =>
                    file instanceof File &&
                    file.name === rawFile?.name &&
                    Number(file.size) === Number(rawFile?.size),
            );
            if (index > -1) {
                dispatch(BillboardActions.removeFileToUpload(index));
            }
        },
        [dispatch, newFiles],
    );

    const groups = useMemo(
        () => [
            {
                title: getLiteral('label_pictures'),
                maxVisible: 3,
                validateFiles: isImageFile,
            },
            {
                title: getLiteral('title_documents'),
                maxVisible: 3,
                validateFiles: isDocumentFile,
            },
        ],
        [],
    );

    return (
        <div
            className={classnames('fm-crud-field', 'fm-crud-field__attachments', {
                [`fm-crud-field__${field.id}`]: !!field.id,
            })}
        >
            <FieldWrapper isFullWidth label={field.label} labelMode={field.labelMode || 'vertical'}>
                <div className="fm-field-crud-upload-files">
                    <FilePicker
                        buttonLabel={getLiteral('label_billboard_add_file')}
                        files={pickerFiles}
                        foldedText={getLiteral('label_see_more')}
                        groups={groups}
                        isFullWidth
                        multiple
                        onDrop={onDrop}
                        onRemove={onRemove}
                        previewImages
                        unfoldedText={getLiteral('label_see_less')}
                    />
                </div>
            </FieldWrapper>
        </div>
    );
});

BillboardAttachmentsField.displayName = 'BillboardAttachmentsField';

BillboardAttachmentsField.propTypes = {
    data: PropTypes.object,
    field: PropTypes.object.isRequired,
};

export default BillboardAttachmentsField;
